import { Injectable } from '@angular/core';
import { User, UserHelper } from '../../models/b2b/user';
import { Product } from '../../models/b2b/product';
import { env } from '../../../../../env';

@Injectable({
    providedIn: 'root'
})
export class SalesforceService {
    env: any;
    constructor() {
        this.env = env;
    }

    /**
     * Initialize the Salesforce SDK
     */
    init() {
        if (!this.env.salesForceOn) return;
        if (typeof window !== 'undefined' && window.SalesforceInteractions) {
            // Initialize the SDK
            window.SalesforceInteractions.init({
                consents: [
                    {
                        purpose: window.SalesforceInteractions.ConsentPurpose.Tracking,
                        provider: 'Custom',
                        status: window.SalesforceInteractions.ConsentStatus.OptIn
                    }
                ],
                cookieDomain: window.location.hostname
            });

            // Set the logging level for debugging purposes
            window.SalesforceInteractions.setLoggingLevel(4);

            console.log('Salesforce SDK Initialized');
        } else {
            console.warn('Salesforce SDK not found. Make sure the script is loaded in index.html');
        }
    }

    /**
     * Track User Profile / Identity
     */
    sendUserIdentity(user: User) {
        if (!this.env.salesForceOn) return;
        if (!window.SalesforceInteractions || !user) return;

        window.SalesforceInteractions.sendEvent({
            interaction: {
                name: 'UpdateProfile'
            },
            user: {
                attributes: {
                    eventType: 'Identity',
                    email: user.connectif_email || user.clienteCodigo + '@gsoledad.com',
                    phoneNumber: user.connectif_movil || '',
                    clienteCodigo: user.clienteCodigo,
                    mercado: user.mercado,
                    provincia: user.connectif_provincia || ''
                }
            }
        });
    }

    /**
     * Track Product View
     */
    trackViewProduct(product: Product) {
        if (!this.env.salesForceOn) return;
        if (!window.SalesforceInteractions || !product) return;

        window.SalesforceInteractions.sendEvent({
            interaction: {
                name: 'ViewProduct',
                catalogObject: {
                    type: 'Product',
                    id: product.info.codigo,
                    attributes: {
                        name: product.info.nombre.trim(),
                        url: window.location.origin + '/dashboard/products/main/' + product.info.codigo,
                        imageUrl: product.info.imagenarticulobig,
                        price: product.rates.base.precioMostrarBusqueda,
                        brand: product.info.marca.trim()
                    }
                }
            }
        });
    }

    /**
     * Track Purchase
     */
    trackPurchase(orderId: string, total: number, items: any[]) {
        if (!this.env.salesForceOn) return;
        if (!window.SalesforceInteractions) return;

        window.SalesforceInteractions.sendEvent({
            interaction: {
                name: 'Purchase',
                order: {
                    id: orderId,
                    totalValue: total,
                    currencyCode: 'EUR',
                    lineItems: items.map((item) => ({
                        catalogObjectType: 'Product',
                        catalogObjectId: item.productId || item.product_id,
                        quantity: item.quantity,
                        price: item.unitPrice || item.price
                    }))
                }
            }
        });
    }

    /**
     * Track Cart State
     */
    trackCart(cartId: string, total: number, items: any[]) {
        if (!this.env.salesForceOn) return;
        if (!window.SalesforceInteractions) return;

        window.SalesforceInteractions.sendEvent({
            interaction: {
                name: 'ViewCart',
                cart: {
                    id: cartId,
                    totalValue: total,
                    lineItems: items.map((item) => ({
                        catalogObjectType: 'Product',
                        catalogObjectId: item.productId || item.product_id,
                        quantity: item.quantity,
                        price: item.unitPrice || item.price
                    }))
                }
            }
        });
    }

    /**
     * Track Generic Page View
     */
    trackPageView(url: string) {
        if (!this.env.salesForceOn) return;
        if (!window.SalesforceInteractions) return;

        window.SalesforceInteractions.sendEvent({
            interaction: {
                name: 'PageView'
            },
            attributes: {
                url: url,
                pageTitle: document.title
            }
        });
    }

    /**
     * Track generic event
     */
    trackEvent(eventName: string, attributes: any = {}) {
        if (!this.env.salesForceOn) return;
        if (!window.SalesforceInteractions) return;

        window.SalesforceInteractions.sendEvent({
            interaction: {
                name: eventName.replace(/\s/g, ''),
                ...attributes
            }
        });
    }
}
